import useSetUsersTemporaryPasswordModal from 'TeamModule/components/SetUsersTemporaryPasswordModal/useSetUsersTemporaryPasswordModal';
import { useCallback, useMemo } from 'react';
import { useResetMemberPasswordModal } from './useResetMemberPasswordModal';
import { useBulkAccountsActivationModal } from './useBulkAccountsActivationModal';
import { ActionMenu, ActionMenuSection, useActionMenu } from 'components/ActionMenu';
import { useBulkUpdateTeamMembersAccessLevel } from 'TeamModule/query/hooks/useBulkUpdateTeamMemberAccessLevel';
import { AccessRolesStructureType } from '@constants';
import { useAccessRoles } from 'hooks/useAccessRoles';
import { useSelector } from 'react-redux';
import { selectTeamMembersHash } from 'selectors';
import { useSetCsmAccountModal } from './useSetCsmAccountModal';
import { buildChelloTierMenuItem, useSetChelloTierModal } from './useSetChelloTierModal';
import { buildSignInStatusMenuItem, useSetSignInStatusModal } from './useSetSignInStatusModal';
import { useGrowthBookFlag } from 'hooks/useGrowthBookFlag';

interface UseTeamMemberTableBulkSelectMenuReturnType {
  openTeamMemberTableBulkSelectMenu: (args: { target: HTMLElement }) => void;
  BulkSelectMenuComponent: React.ReactElement;
}

interface UseTeamMemberTableBulkSelectMenuArgs {
  teamId: number;
  accountIds: number[];
  onConfirm: () => void;
  /** Tenant name for API context - ensures actions target the correct tenant */
  tenant?: string;
  /** Realm ID for API context - ensures actions target the correct realm */
  realmId?: string;
}

interface UseTeamMemberTableBulkSelectMenu {
  (args: UseTeamMemberTableBulkSelectMenuArgs): UseTeamMemberTableBulkSelectMenuReturnType;
}

export const useTeamMemberTableBulkSelectMenu: UseTeamMemberTableBulkSelectMenu = ({
  teamId,
  accountIds,
  onConfirm,
  tenant,
  realmId,
}) => {
  const membersHash = useSelector(selectTeamMembersHash);
  // `membersHash` only holds the current page (GLX-1740), so guard against an
  // account id that isn't resolvable rather than sending `undefined` ids.
  const membershipIdsArray = accountIds
    .map((accountId) => membersHash[accountId]?.team_membership_id)
    .filter((id): id is number => id != null);
  const { openSetUsersTemporaryPasswordModal } = useSetUsersTemporaryPasswordModal({
    tenant,
    realmId,
  });
  const { anchorEl, anchorPosition, isOpen, openMenu, closeMenu } = useActionMenu();
  const { accessRolesStructure } = useAccessRoles();

  const { openResetMembersPasswordModal } = useResetMemberPasswordModal({ tenant, realmId });
  const { openBulkAccountsActivationModal } = useBulkAccountsActivationModal({ tenant, realmId });
  const { openSetCsmAccountModal } = useSetCsmAccountModal({ tenant, realmId });
  const { openSetChelloTierModal } = useSetChelloTierModal();
  const { openSetSignInStatusModal, batchLimit: signInStatusBatchLimit } =
    useSetSignInStatusModal();
  const { isOn: isSignInStatusOn } = useGrowthBookFlag('memberManagementTableV2Flag');
  const { bulkUpdateTeamMembersAccessLevel } = useBulkUpdateTeamMembersAccessLevel({
    tenant,
    realmId,
  });

  // Memoize the getOption function with stable dependencies
  const getOption = useMemo(() => {
    const createOption = ({
      label,
      subRoles,
      roleId,
      shortLabel,
    }: AccessRolesStructureType): any => {
      return {
        label: shortLabel ? shortLabel : label,
        subItems: subRoles?.map((role) => createOption(role)),
        onClick: roleId
          ? () => {
              // Toast is driven by the mutation hook's onSuccess/onError so
              // failures (e.g. last-admin guard from rails) aren't masked by a
              // premature success message.
              bulkUpdateTeamMembersAccessLevel({
                team_id: teamId,
                team_membership_ids: membershipIdsArray,
                team_role: roleId,
              });
              onConfirm();
            }
          : undefined,
      };
    };
    return createOption;
  }, [bulkUpdateTeamMembersAccessLevel, membershipIdsArray, teamId, onConfirm]);

  const openTeamMemberTableBulkSelectMenu = useCallback<
    UseTeamMemberTableBulkSelectMenuReturnType['openTeamMemberTableBulkSelectMenu']
  >(
    ({ target }) => {
      openMenu(target);
    },
    [openMenu],
  );

  const sections: ActionMenuSection[] = [
    {
      title: 'Password Management',
      items: [
        {
          label: 'Request Password Reset',
          onClick: () =>
            openResetMembersPasswordModal({
              accountIds,
              onConfirm: () => {
                onConfirm();
              },
            }),
        },
        {
          label: 'Bulk Set Temporary Password',
          onClick: () => {
            openSetUsersTemporaryPasswordModal({
              accountIds,
              teamId,
            });
          },
        },
      ],
    },
    {
      title: 'Account Management',
      items: [
        {
          label: 'Mark as CSM Account',
          onClick: () => openSetCsmAccountModal({ accountId: accountIds[0] }),
          disabled: accountIds.length > 1,
        },
        {
          label: 'Bulk Accounts Activation',
          onClick: () =>
            openBulkAccountsActivationModal({
              accountIds,
            }),
        },
        // GLX-2012, beside Bulk Accounts Activation
        ...(isSignInStatusOn
          ? [
              buildSignInStatusMenuItem({
                enabled:
                  membershipIdsArray.length > 0 &&
                  membershipIdsArray.length === accountIds.length &&
                  membershipIdsArray.length <= signInStatusBatchLimit,
                onSelect: (loginEnabled) =>
                  openSetSignInStatusModal({
                    tenant,
                    realmId,
                    teamId,
                    loginEnabled,
                    updateLoadedRows: true,
                    members: accountIds.flatMap((accountId) => {
                      const member = membersHash[accountId];
                      return member?.team_membership_id
                        ? [
                            {
                              teamMembershipId: member.team_membership_id,
                              name: member.name || member.email,
                            },
                          ]
                        : [];
                    }),
                  }),
              }),
            ]
          : []),
      ],
    },
    {
      title: 'Role Management',
      items: [
        {
          label: 'Modify Access Level',
          subItems: accessRolesStructure.map(getOption),
        },
        buildChelloTierMenuItem({
          enabled: true,
          onSelect: (tier) => openSetChelloTierModal({ tenant, realmId, teamId, accountIds, tier }),
        }),
      ],
    },
  ];

  return {
    openTeamMemberTableBulkSelectMenu,
    BulkSelectMenuComponent: (
      <ActionMenu
        anchorEl={anchorEl}
        anchorPosition={anchorPosition}
        open={isOpen}
        onClose={closeMenu}
        sections={sections}
        title="Bulk Actions"
      />
    ),
  };
};
